/**
 * CanvasExportControls — reusable media export buttons for Canvas visualizations.
 *
 * Drop this component next to any Canvas-based visualization to add
 * "Save PNG" and "Record WebM" export buttons. Pass a ref to the canvas
 * element and optionally customize the recording duration.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import React, { useState, useCallback, useRef, useEffect } from "react";
import { capturePng, captureWebM, downloadBlob } from "../utils/canvasCapture.js";

export interface CanvasExportControlsProps {
  /** Ref to the canvas element to capture from. */
  canvasRef: React.RefObject<HTMLCanvasElement | null>;
  /** Base filename (without extension) for downloads. */
  filename: string;
  /** WebM recording duration in seconds (default: 5). */
  recordDuration?: number;
  /** Whether WebM recording is available (default: true for animated canvases). */
  allowVideo?: boolean;
}

export function CanvasExportControls({
  canvasRef,
  filename,
  recordDuration = 5,
  allowVideo = true,
}: CanvasExportControlsProps): React.JSX.Element {
  const [recording, setRecording] = useState(false);
  const [progress, setProgress] = useState("");

  // INV.PRESERVE: track mounted state so state setters are not called after unmount.
  // Clears the interval and prevents React warnings/leaks when the parent tab switches.
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return (): void => {
      mountedRef.current = false;
    };
  }, []);

  const handlePng = useCallback(async () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    try {
      const blob = await capturePng(canvas);
      downloadBlob(blob, `${filename}.png`);
    } catch {
      setProgress("PNG capture failed");
    }
  }, [canvasRef, filename]);

  const handleWebM = useCallback(async () => {
    const canvas = canvasRef.current;
    if (!canvas || recording) return;
    setRecording(true);
    setProgress(`Recording ${recordDuration}s…`);

    // Countdown display — cleared on all exit paths
    let remaining = recordDuration;
    const timer = setInterval(() => {
      remaining--;
      if (remaining > 0 && mountedRef.current) setProgress(`Recording ${remaining}s…`);
    }, 1000);

    try {
      const blob = await captureWebM(canvas, { duration: recordDuration });
      clearInterval(timer);
      downloadBlob(blob, `${filename}.webm`);
      if (mountedRef.current) {
        setProgress("Done!");
        setTimeout(() => {
          if (mountedRef.current) setProgress("");
        }, 2000);
      }
    } catch {
      clearInterval(timer);
      if (mountedRef.current) setProgress("Recording failed");
    } finally {
      if (mountedRef.current) setRecording(false);
    }
  }, [canvasRef, filename, recordDuration, recording]);

  const btnStyle: React.CSSProperties = {
    background: "#1a2332",
    color: "#c8d4e0",
    border: "1px solid #2a3444",
    borderRadius: "6px",
    padding: "0.35rem 0.7rem",
    fontSize: "0.7rem",
    cursor: "pointer",
  };

  const recordingStyle: React.CSSProperties = {
    ...btnStyle,
    background: recording ? "#dc2626" : "#1a2332",
    color: recording ? "#fff" : "#c8d4e0",
    border: recording ? "1px solid #dc2626" : "1px solid #2a3444",
    cursor: recording ? "not-allowed" : "pointer",
  };

  return (
    <div style={{ display: "flex", gap: "0.4rem", alignItems: "center", flexWrap: "wrap" }}>
      <button
        type="button"
        onClick={() => {
          void handlePng();
        }}
        style={btnStyle}
      >
        📷 Save PNG
      </button>
      {allowVideo && (
        <button
          type="button"
          onClick={() => {
            void handleWebM();
          }}
          disabled={recording}
          style={recordingStyle}
        >
          {recording ? "⏺ Recording…" : "🎬 Record WebM"}
        </button>
      )}
      {progress && (
        <span
          style={{
            fontSize: "0.65rem",
            color: progress.includes("fail") ? "#f87171" : "#6b7a8d",
            fontFamily: "'JetBrains Mono', monospace",
          }}
        >
          {progress}
        </span>
      )}
    </div>
  );
}
